import { axiosInstance } from "@/lib/axios";
import axios from "axios";
import { ZodError } from "zod";
import {
  fileUploadRequestSchema,
  fileUploadResponseSchema,
  type FileUploadRequest,
  type FileUploadResponse
} from "../types/file-upload.schema";
import { API_ENDPOINTS, HTTP_STATUS } from "@/lib/api-constants";

/**
 * Request a presigned URL for file upload
 * @param uploadRequest - File upload request data
 * @returns Promise resolving to upload response with presigned URL
 */
export const requestFileUpload = async (uploadRequest: FileUploadRequest): Promise<FileUploadResponse> => {
  try {
    const validatedData = fileUploadRequestSchema.parse(uploadRequest);
    const response = await axiosInstance.post(API_ENDPOINTS.FILES.REQUEST_UPLOAD, validatedData);
    const validatedResponse = fileUploadResponseSchema.parse(response.data);

    return validatedResponse;
  } catch (error) {
    if (error instanceof ZodError) {
      const errorMessages = error.issues.map((issue) => issue.message).join(', ');
      throw new Error(`Invalid request data: ${errorMessages}`);
    }

    if (error && typeof error === 'object' && 'response' in error) {
      const axiosError = error as {
        response?: {
          status?: number;
          data?: { message?: string };
        };
        message?: string;
      };
      const status = axiosError.response?.status;
      const message = axiosError.response?.data?.message || axiosError.message || 'Unknown error';

      switch (status) {
        case HTTP_STATUS.BAD_REQUEST:
          throw new Error(`Invalid request: ${message}`);
        case HTTP_STATUS.UNAUTHORIZED:
          throw new Error('Unauthorized to upload files');
        case HTTP_STATUS.FORBIDDEN:
          throw new Error('Forbidden to upload files');
        case HTTP_STATUS.UNPROCESSABLE_ENTITY:
          throw new Error(`File validation failed: ${message}`);
        case HTTP_STATUS.TOO_MANY_REQUESTS:
          throw new Error('Too many upload requests. Please try again later.');
        default:
          throw new Error(message || 'Failed to request upload URL. Please try again.');
      }
    }

    throw new Error('Network error. Please check your connection and try again.');
  }
};

/**
 * Upload file to presigned URL
 * @param uploadUrl - Presigned URL for upload
 * @param file - File to upload
 * @param onProgress - Optional progress callback
 * @returns Promise resolving when upload is complete
 */
export const uploadFileToPresignedUrl = async (
  uploadUrl: string,
  file: File,
  onProgress?: (progress: { loaded: number; total: number; percentage: number }) => void
): Promise<void> => {
  try {
    await axios.put(uploadUrl, file, {
      headers: {
        'Content-Type': file.type,
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const percentage = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress({
            loaded: progressEvent.loaded,
            total: progressEvent.total,
            percentage,
          });
        }
      },
    });
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const status = error.response?.status;
      const message = error.response?.data?.message || error.message || 'Unknown error';

      if (status && status >= 400 && status < 500) {
        throw new Error(`Upload failed with status ${status}: ${message}`);
      } else if (status && status >= 500) {
        throw new Error(`Server error during upload: ${message}`);
      } else {
        throw new Error(`Upload error: ${message}`);
      }
    }

    throw new Error('Network error during file upload');
  }
};

/**
 * Commit file upload after successful upload to presigned URL
 * @param fileId - File ID from the request upload response
 * @returns Promise resolving when commit is complete
 */
export const commitFileUpload = async (fileId: number): Promise<void> => {
  try {
    await axiosInstance.put(API_ENDPOINTS.FILES.COMMIT_UPLOAD, {
      file_id: fileId,
    });
  } catch (error) {
    if (error && typeof error === 'object' && 'response' in error) {
      const axiosError = error as {
        response?: {
          status?: number;
          data?: { message?: string };
        };
        message?: string;
      };
      const status = axiosError.response?.status;
      const message = axiosError.response?.data?.message || axiosError.message || 'Unknown error';

      switch (status) {
        case HTTP_STATUS.BAD_REQUEST:
          throw new Error(`Invalid request: ${message}`);
        case HTTP_STATUS.UNAUTHORIZED:
          throw new Error('Unauthorized to commit file upload');
        case HTTP_STATUS.FORBIDDEN:
          throw new Error('Forbidden to commit file upload');
        case HTTP_STATUS.NOT_FOUND:
          throw new Error('File not found');
        default:
          throw new Error(message || 'Failed to commit file upload. Please try again.');
      }
    }

    throw new Error('Network error. Please check your connection and try again.');
  }
};

/**
 * Complete file upload process: request presigned URL and upload file
 * @param file - File to upload
 * @param sourceModule - Module identifier for the upload
 * @param onProgress - Optional progress callback
 * @returns Promise resolving to file upload info
 */
export const uploadFile = async (
  file: File,
  sourceModule: FileUploadRequest['source_module'],
  onProgress?: (progress: { loaded: number; total: number; percentage: number }) => void
): Promise<{
  file_id: number;
  object_key: string;
  filename: string;
  size_bytes: number;
  mime_type: string;
}> => {
    const uploadRequest: FileUploadRequest = {
      filename: file.name,
      mime_type: file.type,
      source_module: sourceModule,
      size_bytes: file.size,
    };

    const uploadResponse = await requestFileUpload(uploadRequest);
    const { upload_url, file_id, object_key } = uploadResponse.response_output.detail;

    await uploadFileToPresignedUrl(upload_url, file, onProgress);

    // Commit the upload after successful upload to presigned URL
    await commitFileUpload(file_id);

    return {
      file_id,
      object_key,
      filename: file.name,
      size_bytes: file.size,
      mime_type: file.type,
    };
};

/**
 * Download file by file ID - returns presigned URL
 * @param fileId - File ID to download
 * @returns Promise resolving to presigned URL and file metadata
 */
export const downloadFile = async (fileId: number): Promise<{
  presignedUrl: string;
  mimeType: string;
  fileId: number;
  file_name: string;
}> => {
  try {
    const response = await axiosInstance.get(API_ENDPOINTS.FILES.DOWNLOAD(fileId));

    const data = response.data;

    return {
      presignedUrl: data.response_output.detail.download_url, // Presigned URL
      mimeType: data.response_output.detail.mime_type, // Actual mime type
      fileId: data.response_output.detail.file_id,
      file_name: data.response_output.detail.file_name,
    };
  } catch (error) {
    if (error && typeof error === 'object' && 'response' in error) {
      const axiosError = error as {
        response?: {
          status?: number;
          data?: { message?: string };
        };
        message?: string;
      };
      const status = axiosError.response?.status;
      const message = axiosError.response?.data?.message || axiosError.message || 'Unknown error';

      switch (status) {
        case HTTP_STATUS.BAD_REQUEST:
          throw new Error(`Invalid request: ${message}`);
        case HTTP_STATUS.UNAUTHORIZED:
          throw new Error('Unauthorized to download file');
        case HTTP_STATUS.FORBIDDEN:
          throw new Error('Forbidden to download file');
        case HTTP_STATUS.NOT_FOUND:
          throw new Error('File not found');
        default:
          throw new Error(message || 'Failed to download file. Please try again.');
      }
    }

    throw new Error('Network error. Please check your connection and try again.');
  }
};